import { useCallback, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { ReactComponent as EditPencil } from "assets/images/icons/EditPencil.svg";
import { getPreferredContact } from "api";
import { useGetAddressesV2 } from "api/queries/company/useGetAddressesV2";
import { useGetAddressesV2Details } from "api/queries/company/useGetAddressV2Details";
import { CreateUpdateCompanyAddressV2ResponseType } from "api/response";
import usePermissions from "hooks/usePermissions";
import Button from "components/atoms/Button/Button";
import AddressV2Dialog from "components/organisms/AddressV2Dialog/AddressV2Dialog";
import AddressLocodeAndCustomFields from "components/organisms/AddressLocodeAndCustomFields/AddressLocodeAndCustomFields";
import { AddressOption, formatAddress } from "../PickupDeliveryAddress/helpers";
import FormItemInfo from "../../Common/FormItemInfo";
import SearchSelect from "../../Common/SearchSelect";

interface DropAddressFormProps {
  index: number;
}

export default function DropAddressForm({ index }: DropAddressFormProps) {
  const intl = useIntl();
  const { permissions }: any = usePermissions();
  const addressBookPermissions = permissions ? JSON.parse(permissions).permissions.quoting : null;

  const [searchInput, setSearchInput] = useState("");
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(undefined);

  const { getValues, setValue, watch } = useFormContext();

  const currentAddressValues = watch(`schedule.stops.${index}`) ?? {};
  const { address_reference_number, address_street_address, address_name } = currentAddressValues;

  const { data: addresses = [], refetch: refetchAddresses } = useGetAddressesV2(
    {
      params: {
        query: searchInput,
        payment_location: getValues("organizational.location"),
      },
    },
    {
      keepPreviousData: true,
    },
  );

  const { data: addressV2Details } = useGetAddressesV2Details(address_reference_number, {
    onFetchSuccess: (address) => handleSelectAddress([address]),
    options: {
      enabled: Boolean(!address_street_address && address_reference_number),
      refetchOnMount: true,
    },
  });

  const options =
    addressV2Details && isInitialRender
      ? [formatAddress(addressV2Details as any)]
      : addresses?.map((address: any) => formatAddress(address));

  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const onInputChange = (val: string) => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    timeoutId.current = setTimeout(() => {
      setSearchInput(val);
      setIsInitialRender(false);
    }, 500);
  };

  const handleSelectAddress = useCallback((event: any[]) => {
    const address = event[0];
    if (!address) return;

    setValue(`schedule.stops.${index}`, {
      ...getValues(`schedule.stops.${index}`),
      address_id: address.id,
      address_reference_number: address.reference_number,
      address_name: address.name,
      street: address.street_address,
      street_line2: address.street_address_line_two,
      timezone: address.timezone,
      city_id: address.city,
      city: address.city_name,
      state_id: address.state,
      state: address.state_name,
      country_id: address.country,
      country: address.country_name,
      zipcode: address.zipcode,
      locode_id: address.locode,
      un_locode: address.un_locode,
      location_reference_number: address.location_reference_number,
      custom_address_reference: address.custom_address_reference,
      preferred_contact: address.preferred_contact,
      address_label: address.address_label,
    });

    getPreferredContact(address?.reference_number).then((res) => {
      setValue(`schedule.stops.${index}.stop_contact_name`, res.data.name, { shouldValidate: true });
      setValue(`schedule.stops.${index}.stop_contact_number`, res.data.phone_number, {
        shouldValidate: true,
      });
      setValue(`schedule.stops.${index}.stop_contact_email`, res.data.email, { shouldValidate: true });
    });
  }, []);

  const handleAddNewAddress = (data: CreateUpdateCompanyAddressV2ResponseType) => {
    if (!data) return;
    handleSelectAddress([data]);
  };

  const checkAddressDuplication = () => {
    const stopAddressIds = watch("schedule.stops").map((stop: any) => stop.address_id);
    stopAddressIds.splice(index, 1);
    const currentStopAddressId = watch(`schedule.stops.${index}.address_id`);
    return !stopAddressIds.some((id: any) => id === currentStopAddressId);
  };

  const handleEditAddress = () => {
    setSelectedAddress({
      ...getValues(`schedule.stops.${index}`),
      name: getValues(`schedule.stops.${index}.address_name`),
      street_address: getValues(`schedule.stops.${index}.street`),
      street_address_line_two: getValues(`schedule.stops.${index}.street_line2`),
    });
    setShowAddressDialog(true);
  };

  return (
    <div className="d-flex mt-3xl mb-3xl">
      <div className="mr-xl" style={{ minWidth: "200px" }}>
        <FormItemInfo
          labelId={`${index === 0 ? "new-request.schedule.pickupAddress" : "new-request.schedule.deliveryAddress"}`}
          label={`${index === 0 ? "Pickup address" : "Delivery address"}`}
        />
      </div>
      <div className="w-100" style={{ maxWidth: "500px" }}>
        <SearchSelect
          name={`schedule.stops.${index}.address_name`}
          preselect={false}
          enableManualValue={false}
          required
          disableAutoClear
          options={options}
          placeholder={intl.formatMessage({
            id: "new-request.schedule.chooseAddressV2Placeholder",
            defaultMessage: "Search by address, location reference number, or custom reference",
          })}
          // disable client side filtering
          filterBy={() => true}
          handleRenderOption={({ option }: any) => <AddressOption option={option} />}
          onInputChange={onInputChange}
          onSelect={handleSelectAddress}
          validations={{
            validate: () =>
              checkAddressDuplication() ||
              intl.formatMessage({
                id: "new-request.schedule.multiDropAddressDuplicationError",
                defaultMessage: "An address cannot be used more than once in a single shipment route.",
              }),
          }}
        />

        <div className="d-flex justify-content-between mt-sm">
          <div>
            {address_name && (
              <div className="d-flex">
                <div className="font-b3">
                  <div className="font-weight-bold">{currentAddressValues?.address_name}</div>
                  <div>{currentAddressValues?.street}</div>
                  <div>{currentAddressValues?.street_line2}</div>
                  <div>
                    {currentAddressValues?.city}
                    {currentAddressValues?.state && `, ${currentAddressValues?.state}`}
                    {currentAddressValues?.country && `, ${currentAddressValues?.country}`} {currentAddressValues?.zipcode}
                  </div>
                  <AddressLocodeAndCustomFields
                    un_locode={currentAddressValues?.un_locode}
                    location_reference_number={currentAddressValues?.location_reference_number}
                    custom_address_reference={currentAddressValues?.custom_address_reference}
                    un_locode_className="font-b4"
                  />
                </div>
                {addressBookPermissions?.allow_address_book_entry && (
                  <div className="align-self-end ml-md cursor-pointer">
                    {false ? (
                      <div className="spinner-border spinner-border-sm" role="status" />
                    ) : (
                      <EditPencil onClick={handleEditAddress} />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          {addressBookPermissions?.allow_address_book_entry && (
            <div className="d-flex">
              <Button variant="link" className="p-xs" onClick={() => setShowAddressDialog(true)}>
                <span className="font-b4">
                  + <FormattedMessage id="new-request.schedule.addNewAddress" defaultMessage="Add new address" />
                </span>
              </Button>
              <AddressV2Dialog
                onClose={() => {
                  setShowAddressDialog(false);
                  setSelectedAddress(undefined);
                }}
                isOpen={showAddressDialog}
                refetchAddresses={refetchAddresses}
                source="quick_quote_request"
                onAddNewAddress={handleAddNewAddress}
                forceCreate
                formSelectedShipperLocation={getValues("organizational.location")}
                selectedAddress={selectedAddress}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
